// ─────────────────────────────────────────────────────────────────────────────
// PLANS DATA — Flat 3-Duration Architecture
// ─────────────────────────────────────────────────────────────────────────────

export type PlanDuration = '30min' | '45min' | '60min'

export type PlanNumbers = {
  pricePerMonth: number // shown as the plan price
  classesPerMonth: number // e.g. 8
  daysPerWeek: number // e.g. 2
  hoursPerMonth: number // e.g. 4, 6, 8
}

export type PlanPricingTable = Record<PlanDuration, PlanNumbers>

// Default/fallback pricing
export const PLAN_PRICING: PlanPricingTable = {
  '30min': {
    pricePerMonth: 32,
    classesPerMonth: 8,
    daysPerWeek: 2,
    hoursPerMonth: 4,
  },
  '45min': {
    pricePerMonth: 48,
    classesPerMonth: 8,
    daysPerWeek: 2,
    hoursPerMonth: 6,
  },
  '60min': {
    pricePerMonth: 65,
    classesPerMonth: 8,
    daysPerWeek: 2,
    hoursPerMonth: 8,
  },
}

export const DURATION_MINUTES: Record<PlanDuration, number> = {
  '30min': 30,
  '45min': 45,
  '60min': 60,
}

export const DURATION_LABELS: Record<PlanDuration, string> = {
  '30min': '30 Minutes',
  '45min': '45 Minutes',
  '60min': '60 Minutes',
}

const SHARED_FEATURES = [
  '1-on-1 Live Quran Sessions',
  'Qualified Quran Teacher',
  'Tajweed Correction & Feedback',
  'Personalized Learning Plan',
  'Flexible Scheduling & Rescheduling',
  'Free Trial Class Included',
  'WhatsApp Progress & Notes Updates',
  'Monthly Progress Report',
  'Daily/Weekly Memorization Goals',
  'Revision & Retention Support',
] as const

export type PlanConfig = {
  id: PlanDuration
  duration: PlanDuration
  name: string
  subtitle: string
  badge?: string
  featured?: boolean
  features: readonly string[]
}

export const PLAN_CONFIGS: PlanConfig[] = [
  {
    id: '30min',
    duration: '30min',
    name: '30 Minutes Session',
    subtitle: 'Standard foundational Tajweed & recitation classes.',
    features: [...SHARED_FEATURES],
  },
  {
    id: '45min',
    duration: '45min',
    name: '45 Minutes Session',
    subtitle: 'Optimal pace for Quran memorization and practical revision.',
    badge: 'Most Popular',
    featured: true,
    features: [...SHARED_FEATURES],
  },
  {
    id: '60min',
    duration: '60min',
    name: '60 Minutes Session',
    subtitle: 'Deep comprehensive Quran study and advanced Ijazah preparation.',
    badge: 'Intensive',
    features: [...SHARED_FEATURES],
  },
]

export type Plan = PlanConfig &
  PlanNumbers & {
    classMinutes: number
  }

export function buildPlans(pricingTable?: any): Plan[] {
  const table =
    pricingTable &&
    typeof pricingTable === 'object' &&
    'data' in pricingTable &&
    pricingTable.data
      ? pricingTable.data
      : (pricingTable as Partial<PlanPricingTable>) || {}

  return PLAN_CONFIGS.map((config) => {
    const numbers = table[config.duration] || PLAN_PRICING[config.duration]
    return {
      ...config,
      ...numbers,
      classMinutes: DURATION_MINUTES[config.duration],
    }
  })
}

export const PLAN_CONTENT = {
  eyebrow: 'TRANSPARENT PRICING',
  title: 'Flexible Plans',
  subtitle: 'For Every Student',
  description:
    'Choose the class duration that fits your schedule. All plans include a free trial class with no commitment required.',
}
